"use client";
import { useCallback, useEffect } from "react";
import { atom, useAtom } from "jotai";
import { toast } from "sonner";
import {
  ColorPalette,
  FontOption,
  LogoGenerationInput,
  PaletteId,
} from "@constructr-sdk/entities";
import { trpcReactQueryApi } from "~/trpc/react";
import type {
  UseLogoGeneratorParams,
  UseLogoGeneratorReturn,
} from "~/modules/main/hooks/use-logo-generator/use-logo-generator.interface";

// Allowed image MIME types for validation
const ALLOWED_IMAGE_MIME_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'] as const;

type AllowedImageMimeType = 'image/png' | 'image/jpeg' | 'image/gif' | 'image/webp';

function toAllowedMimeType(mime: string | null): AllowedImageMimeType | null {
  if (!mime) return null;
  const allowed = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'] as const;
  return (allowed as readonly string[]).includes(mime) ? (mime as AllowedImageMimeType) : null;
}

// Hoist atom definitions to top level to prevent recreation on each render
const promptAtom = atom<string>("");
const styleImageFileAtom = atom<File | null>(null);
const styleImageUrlAtom = atom<string | null>(null);
const selectedPaletteIdAtom = atom<PaletteId>(PaletteId.NEON);
const selectedFontIdAtom = atom<string>("press-start-2p");
const sizeAtom = atom<number>(512);
const svgMarkupAtom = atom<string | null>(null);
const isGeneratingAtom = atom<boolean>(false);
const urlValidationStatusAtom = atom<
  "idle" | "validating" | "valid" | "invalid"
>("idle");
const errorAtom = atom<string | null>(null);

// Default palette options based on the design requirements
const DEFAULT_PALETTE_OPTIONS: ColorPalette[] = [
  {
    id: PaletteId.NEON,
    name: "Neon",
    swatches: ["#ff2bd3", "#0ffbdf", "#7b2cbf", "#00f5ff"],
  },
  {
    id: PaletteId.MONO,
    name: "Monochrome",
    swatches: ["#000000", "#ffffff", "#808080", "#404040"],
  },
  {
    id: PaletteId.WARM,
    name: "Warm",
    swatches: ["#ff6b35", "#f7931e", "#fdc500", "#c1121f"],
  },
  {
    id: PaletteId.COOL,
    name: "Cool",
    swatches: ["#4361ee", "#3a0ca3", "#7209b7", "#560bad"],
  },
  {
    id: PaletteId.PASTEL,
    name: "Pastel",
    swatches: ["#ffc8dd", "#bde0fe", "#a2d2ff", "#cdb4db"],
  },
];

// Default font options
const DEFAULT_FONT_OPTIONS: FontOption[] = [
  {
    id: "press-start-2p",
    name: "Press Start 2P",
    cssFamily: "'Press Start 2P', cursive",
  },
  {
    id: "dm-sans",
    name: "DM Sans",
    cssFamily: "'DM Sans', sans-serif",
  },
  {
    id: "roboto",
    name: "Roboto",
    cssFamily: "'Roboto', sans-serif",
  },
  {
    id: "inter",
    name: "Inter",
    cssFamily: "'Inter', sans-serif",
  },
];

/**
 * Validates if a string contains valid SVG markup
 */
function isValidSvg(markup: string): boolean {
  if (!markup || typeof markup !== "string") return false;
  const trimmed = markup.trim();
  // Check if it starts with <svg and contains closing tag
  return trimmed.startsWith("<svg") && trimmed.includes("</svg>");
}

/**
 * Sanitizes SVG markup by removing potentially dangerous elements and attributes
 */
function sanitizeSvg(markup: string): string {
  // Remove script tags and their content
  let sanitized = markup.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "");
  // Remove on* event handlers
  sanitized = sanitized.replace(/\son\w+\s*=\s*["'][^"']*["']/gi, "");
  // Remove javascript: protocol
  sanitized = sanitized.replace(/javascript:/gi, "");
  // Remove foreignObject elements
  sanitized = sanitized.replace(/<foreignObject\b[^<]*(?:(?!<\/foreignObject>)<[^<]*)*<\/foreignObject>/gi, "");
  return sanitized;
}

/**
 * Converts a File to base64 string (without data: prefix)
 */
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the data:image/...;base64, prefix
      const base64 = result.split(',')[1];
      if (!base64) {
        reject(new Error('Failed to convert file to base64'));
        return;
      }
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export function useLogoGenerator(
  params: UseLogoGeneratorParams,
): UseLogoGeneratorReturn {
  // Global state atoms
  const [prompt, setPromptState] = useAtom(promptAtom);
  const [styleImageFile, setStyleImageFileState] = useAtom(styleImageFileAtom);
  const [styleImageUrl, setStyleImageUrlState] = useAtom(styleImageUrlAtom);
  const [selectedPaletteId, setSelectedPaletteIdState] = useAtom(selectedPaletteIdAtom);
  const [selectedFontId, setSelectedFontIdState] = useAtom(selectedFontIdAtom);
  const [size, setSizeState] = useAtom(sizeAtom);
  const [svgMarkup, setSvgMarkupState] = useAtom(svgMarkupAtom);
  const [isGenerating, setIsGenerating] = useAtom(isGeneratingAtom);
  const [urlValidationStatus, setUrlValidationStatus] = useAtom(urlValidationStatusAtom);
  const [error, setErrorState] = useAtom(errorAtom);

  // Initialize defaults from params
  useEffect(() => {
    if (params.defaults?.paletteId) {
      setSelectedPaletteIdState(params.defaults.paletteId);
    }
    if (params.defaults?.fontId) {
      setSelectedFontIdState(params.defaults.fontId);
    }
    if (params.defaults?.size) {
      setSizeState(params.defaults.size);
    }
  }, [params.defaults, setSelectedPaletteIdState, setSelectedFontIdState, setSizeState]);

  // TRPC mutation for logo generation
  const generateLogoMutation = trpcReactQueryApi.main.logoGenerator.generateLogo.useMutation({
    onSuccess: (data) => {
      if (!isValidSvg(data.svgMarkup)) {
        setErrorState("Generated content is not valid SVG");
        setIsGenerating(false);
        toast.error("Generated content is not valid SVG");
        return;
      }
      const sanitized = sanitizeSvg(data.svgMarkup);
      setSvgMarkupState(sanitized);
      setIsGenerating(false);
      setErrorState(null);
      toast.success("Logo generated successfully!");
    },
    onError: (err) => {
      setErrorState(err.message || "Failed to generate logo");
      setIsGenerating(false);
      toast.error(err.message || "Failed to generate logo");
    },
  });

  // TRPC mutation for URL validation
  const validateImageUrlMutation = trpcReactQueryApi.main.logoGenerator.validateImageUrl.useMutation();

  // Mutations (synchronous state updates)
  const setPrompt = useCallback(
    (newPrompt: string) => {
      setPromptState(newPrompt);
    },
    [setPromptState],
  );

  const setStyleImageFile = useCallback(
    (file: File | null) => {
      if (file) {
        // Validate MIME type
        if (!ALLOWED_IMAGE_MIME_TYPES.includes(file.type as any)) {
          setErrorState('Unsupported image type. Supported: PNG, JPG, GIF, WEBP');
          toast.error('Unsupported image type. Supported: PNG, JPG, GIF, WEBP');
          setStyleImageFileState(null);
          setStyleImageUrlState(null);
          return;
        }
        // Validate file size (10 MB max)
        if (file.size > 10 * 1024 * 1024) {
          setErrorState('Image too large (max 10 MB)');
          toast.error('Image too large (max 10 MB)');
          setStyleImageFileState(null);
          setStyleImageUrlState(null);
          return;
        }
      }
      setStyleImageFileState(file);
      // Clear URL when file is set
      if (file) {
        setStyleImageUrlState(null);
      }
      setErrorState(null);
    },
    [setStyleImageFileState, setStyleImageUrlState, setErrorState],
  );

  const setStyleImageUrl = useCallback(
    (url: string | null) => {
      setStyleImageUrlState(url);
      // Clear file when URL is set
      if (url) {
        setStyleImageFileState(null);
      }
      // Reset validation status when URL changes
      setUrlValidationStatus("idle");
    },
    [setStyleImageUrlState, setStyleImageFileState, setUrlValidationStatus],
  );

  const setSelectedPaletteId = useCallback(
    (id: PaletteId) => {
      setSelectedPaletteIdState(id);
    },
    [setSelectedPaletteIdState],
  );

  const setSelectedFontId = useCallback(
    (id: string) => {
      setSelectedFontIdState(id);
    },
    [setSelectedFontIdState],
  );

  const setSize = useCallback(
    (newSize: number) => {
      setSizeState(newSize);
    },
    [setSizeState],
  );

  const resetAll = useCallback(() => {
    setPromptState("");
    setStyleImageFileState(null);
    setStyleImageUrlState(null);
    setSelectedPaletteIdState(params.defaults?.paletteId ?? PaletteId.NEON);
    setSelectedFontIdState(params.defaults?.fontId ?? "press-start-2p");
    setSizeState(params.defaults?.size ?? 512);
    setSvgMarkupState(null);
    setErrorState(null);
    setUrlValidationStatus("idle");
  }, [
    setPromptState,
    setStyleImageFileState,
    setStyleImageUrlState,
    setSelectedPaletteIdState,
    setSelectedFontIdState,
    setSizeState,
    setSvgMarkupState,
    setErrorState,
    setUrlValidationStatus,
    params.defaults,
  ]);

  const clearError = useCallback(() => {
    setErrorState(null);
  }, [setErrorState]);

  // Commands (async operations)
  const validateStyleImageUrl = useCallback(async (): Promise<boolean> => {
    if (!styleImageUrl) {
      setUrlValidationStatus("idle");
      return false;
    }

    setUrlValidationStatus("validating");
    try {
      const result = await validateImageUrlMutation.mutateAsync({ url: styleImageUrl });
      
      if (result.isImage && result.mimeType && ALLOWED_IMAGE_MIME_TYPES.includes(result.mimeType as any)) {
        setUrlValidationStatus("valid");
        setErrorState(null);
        return true;
      } else {
        setUrlValidationStatus("invalid");
        setErrorState("Image URL is not reachable or has unsupported type.");
        toast.error("Image URL is not reachable or has unsupported type.");
        return false;
      }
    } catch (err) {
      setUrlValidationStatus("invalid");
      setErrorState("Unable to validate image URL.");
      toast.error("Unable to validate image URL.");
      return false;
    }
  }, [styleImageUrl, setUrlValidationStatus, setErrorState, validateImageUrlMutation]);

  const generateLogo = useCallback(async (): Promise<void> => {
    if (!prompt.trim()) {
      setErrorState("Please enter a prompt");
      toast.error("Please enter a prompt");
      return;
    }

    setIsGenerating(true);
    setErrorState(null);

    let inputStyleImageBase64: string | null = null;
    let inputStyleImageMimeType: string | null = null;
    let inputStyleImageUrl: string | null = null;
    let inputStyleImageFileName: string | null = null;

    // Handle local file upload
    if (styleImageFile) {
      try {
        inputStyleImageBase64 = await fileToBase64(styleImageFile);
        inputStyleImageMimeType = styleImageFile.type;
        inputStyleImageFileName = styleImageFile.name;
      } catch (err) {
        setErrorState("Failed to process image file");
        toast.error("Failed to process image file");
        setIsGenerating(false);
        return;
      }
    } 
    // Handle remote URL
    else if (styleImageUrl) {
      const isValid = await validateStyleImageUrl();
      if (!isValid) {
        setIsGenerating(false);
        return;
      }
      inputStyleImageUrl = styleImageUrl;
    }

    const input: LogoGenerationInput = {
      prompt: prompt.trim(),
      styleImageUrl: inputStyleImageUrl,
      styleImageFileName: inputStyleImageFileName,
      paletteId: selectedPaletteId,
      size,
      fontId: selectedFontId,
      styleImageBase64: inputStyleImageBase64,
      styleImageMimeType: toAllowedMimeType(inputStyleImageMimeType),
    };

    generateLogoMutation.mutate(input);
  }, [
    prompt,
    styleImageFile,
    styleImageUrl,
    selectedPaletteId,
    size,
    selectedFontId,
    setIsGenerating,
    setErrorState,
    validateStyleImageUrl,
    generateLogoMutation,
  ]);

  const copySvgToClipboard = useCallback(async (): Promise<void> => {
    if (!svgMarkup) {
      toast.error("No SVG to copy");
      return;
    }
    try {
      await navigator.clipboard.writeText(svgMarkup);
      toast.success("SVG copied to clipboard!");
    } catch (err) {
      toast.error("Failed to copy SVG to clipboard");
      setErrorState("Failed to copy SVG to clipboard");
    }
  }, [svgMarkup, setErrorState]);

  const openSvgInNewTab = useCallback((): void => {
    if (!svgMarkup) {
      return;
    }
    const blob = new Blob([svgMarkup], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
    // Clean up the object URL after a delay
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, [svgMarkup]);

  const downloadSvg = useCallback(
    async (filenameSuggestion?: string): Promise<string> => {
      if (!svgMarkup) {
        return "";
      }
      const filename = filenameSuggestion || `logo-${Date.now()}.svg`;
      const blob = new Blob([svgMarkup], { type: "image/svg+xml" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Clean up the object URL
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
      return filename;
    },
    [svgMarkup],
  );

  return {
    // State
    prompt,
    styleImageFile,
    styleImageUrl,
    paletteOptions: DEFAULT_PALETTE_OPTIONS,
    fontOptions: DEFAULT_FONT_OPTIONS,
    selectedPaletteId,
    selectedFontId,
    size,
    svgMarkup,
    isGenerating,
    urlValidationStatus,
    error,

    // Mutations
    setPrompt,
    setStyleImageFile,
    setStyleImageUrl,
    setSelectedPaletteId,
    setSelectedFontId,
    setSize,
    resetAll,
    clearError,

    // Commands
    validateStyleImageUrl,
    generateLogo,
    copySvgToClipboard,
    openSvgInNewTab,
    downloadSvg,
  };
}
